import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Contact.css';

const Contact = () => {
    const [contactData, setContactData] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
    });
    const [loading, setLoading] = useState(true);
    const [submitStatus, setSubmitStatus] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/contact`);
                setContactData(response.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching contact data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        
        try {
            // Ensure the form data matches the expected format on the backend
            const formPayload = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                service: formData['service interest'] || formData.service, // Map service interest to service
                message: formData.message
            };
            
            await axios.post(`${process.env.REACT_APP_API_URL}/api/contact/submit`, formPayload);
            setSubmitStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                service: '',
                message: ''
            });
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (!contactData) return <div className="error">Failed to load contact information</div>;

    return (
        <div className="contact-container">
            <section className="contact-hero">
                <h1>{contactData.hero.title}</h1>
                <p>{contactData.hero.subtitle}</p>
            </section>

            <div className="contact-content">
                <section className="contact-info">
                    <div className="info-card address">
                        <h3>Corporate Office</h3>
                        <p>{contactData.contact_info.address.corporate.street}</p>
                        <p>{contactData.contact_info.address.corporate.city}, {contactData.contact_info.address.corporate.state}</p>
                        <p>{contactData.contact_info.address.corporate.pincode}</p>
                        <p>{contactData.contact_info.address.corporate.country}</p>
                    </div>

                    <div className="info-card address">
                        <h3>Branch Office</h3>
                        <p>{contactData.contact_info.address.branch.street}</p>
                        <p>{contactData.contact_info.address.branch.city}, {contactData.contact_info.address.branch.state}</p>
                        <p>{contactData.contact_info.address.branch.pincode}</p>
                        <p>{contactData.contact_info.address.branch.country}</p>
                    </div>

                    <div className="info-card contact-details">
                        <h3>Contact Details</h3>
                        <p>Main: <a href={`tel:${contactData.contact_info.phone.main.replace(/\s+/g, '')}`}>{contactData.contact_info.phone.main}</a></p>
                        <p>Support: <a href={`tel:${contactData.contact_info.phone.support.replace(/\s+/g, '')}`}>{contactData.contact_info.phone.support}</a></p>
                        <p>Email: <a href={`mailto:${contactData.contact_info.email.general}`}>{contactData.contact_info.email.general}</a></p>
                    </div>

                    <div className="info-card hours">
                        <h3>Office Hours</h3>
                        <p>{contactData.office_hours.weekdays}</p>
                        <p>{contactData.office_hours.saturday}</p>
                        <p>{contactData.office_hours.sunday}</p>
                    </div>

                    <div className="info-card social">
                        <h3>Connect With Us</h3>
                        <div className="social-links">
                            {Object.entries(contactData.social_media).map(([platform, url]) => (
                                <a key={platform} href={url} target="_blank" rel="noopener noreferrer">
                                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="contact-form">
                    <h2>Send us a Message</h2>
                    <form onSubmit={handleSubmit}>
                        {contactData.form_fields.map((field) => {
                            // Convert field label to lowercase for use as name attribute
                            const fieldName = field.label.toLowerCase();
                            
                            return (
                                <div key={field.label} className="form-group">
                                    <label htmlFor={fieldName}>
                                        {field.label}{field.required && <span className="required">*</span>}
                                    </label>
                                    {field.type === 'select' ? (
                                        <select
                                            name={fieldName}
                                            value={formData[fieldName] || ''}
                                            onChange={handleInputChange}
                                            required={field.required}
                                        >
                                            <option value="">Select a service</option>
                                            {field.options.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    ) : field.type === 'textarea' ? (
                                        <textarea
                                            name={fieldName}
                                            value={formData[fieldName] || ''}
                                            onChange={handleInputChange}
                                            required={field.required}
                                            rows="4"
                                        />
                                    ) : (
                                        <input
                                            type={field.type}
                                            name={fieldName}
                                            value={formData[fieldName] || ''}
                                            onChange={handleInputChange}
                                            required={field.required}
                                        />
                                    )}
                                </div>
                            );
                        })}
                        <button type="submit" disabled={submitStatus === 'submitting'}>
                            {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                    {submitStatus === 'success' && (
                        <div className="success-message">
                            Thank you for your message! We'll get back to you soon.
                        </div>
                    )}
                    {submitStatus === 'error' && (
                        <div className="error-message">
                            Sorry, there was an error sending your message. Please try again.
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Contact; 